import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightIcon, MapIcon, PaperAirplaneIcon, BoltIcon, ClockIcon } from '@heroicons/react/24/outline';
import { ArrowRight, Sparkles, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { projectAPI, chatAPI, exploreAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../config/runtime';
import { useMobile } from '../hooks/useMobile';


const normalizeQuickStartText = (value) => {
  if (!value) return '';
  return value.replace(/\s+/g, ' ').trim();
};

const sentenceCase = (value) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const buildQuickStartProjectPayload = (inputValue, isSurprise = false) => {
  const fallbackPrompt = 'Spontaneous nature quest';
  const normalized = normalizeQuickStartText(inputValue) || fallbackPrompt;
  const trimmedTitle = normalized.length > 80 ? `${normalized.slice(0, 77).trim()}...` : normalized;
  const title = sentenceCase(trimmedTitle);
  const description = `${sentenceCase(normalized)}`;

  return {
    title,
    payload: {
      name: title,
      description,
      tags: [],
      settings: {
        archetype: 'site',
        quickStart: true,
        quickStartPrompt: normalized,
        quickStartSource: isSurprise ? 'surprise_me' : 'new_page',
      },
      prompt: normalized,
      startNow: true,
      hasEndTime: false,
      isMultiplayer: true,
      maxParticipants: 50,
      allowLateJoin: true,
      gameMode: 'endless',
    },
  };
};

const extractProjectFromResponse = (response) => {
  const data = response?.data;
  if (!data) return null;
  if (data.data?.project) return data.data.project;
  if (data.project) return data.project;
  if (data.data) return data.data;
  return data;
};

const extractChatIdFromResponse = (response) => {
  return response?.data?.data?.chatId || response?.data?.chatId || null;
};

// Shuffle array utility
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Generate relevance hint based on starter metadata
const getRelevanceHint = (starter, index) => {
  // Check rating/popularity
  const ratingCount = starter.rating?.count || 0;
  if (ratingCount > 10) {
    return `Started by ${ratingCount}+ others`;
  }
  if (ratingCount > 0) {
    return `Started by ${ratingCount} others`;
  }
  
  // Check for seasonal tags
  const tags = starter.tags || [];
  const month = new Date().getMonth();
  const seasonalTags = {
    spring: ['spring', 'bloom', 'flower', 'bird', 'nest'],
    summer: ['summer', 'insect', 'butterfly', 'bee', 'garden'],
    fall: ['fall', 'autumn', 'mushroom', 'fungi', 'leaf'],
    winter: ['winter', 'evergreen', 'pine', 'bark', 'lichen'],
  };
  const currentSeason = month >= 2 && month <= 4 ? 'spring' : 
                        month >= 5 && month <= 7 ? 'summer' :
                        month >= 8 && month <= 10 ? 'fall' : 'winter';
  
  const hasSeasonalTag = tags.some(tag => 
    seasonalTags[currentSeason]?.some(s => tag.toLowerCase().includes(s))
  );
  if (hasSeasonalTag) {
    return 'Common this time of year';
  }
  
  // Check location
  if (starter.formattedLocation) {
    return 'Based on nearby discoveries';
  }
  
  // Check difficulty
  if (starter.difficulty === 'easy') {
    return 'Quick and simple';
  }
  
  // Default hints based on position
  const defaultHints = [
    'Featured starter',
    'Popular with explorers',
    'Great for beginners',
  ];
  return defaultHints[index % defaultHints.length];
};

// Get icon for starter based on tags/name
const getStarterIcon = (starter) => {
  const name = (starter.name || '').toLowerCase();
  const tags = (starter.tags || []).map(t => t.toLowerCase());
  const allText = `${name} ${tags.join(' ')}`;
  
  if (allText.includes('mushroom') || allText.includes('fungi')) return '🍄';
  if (allText.includes('bird') || allText.includes('nest')) return '🪺';
  if (allText.includes('tree') || allText.includes('forest')) return '🌳';
  if (allText.includes('flower') || allText.includes('bloom')) return '🌸';
  if (allText.includes('insect') || allText.includes('bug')) return '🐜';
  if (allText.includes('garden') || allText.includes('plant')) return '🌿';
  if (allText.includes('rock') || allText.includes('moss')) return '🪨';
  if (allText.includes('water') || allText.includes('stream')) return '💧';
  if (allText.includes('repair') || allText.includes('fix') || allText.includes('maintenance')) return '🔧';
  if (allText.includes('photo')) return '📸';
  
  // Default icons cycle
  const defaultIcons = ['🌿', '🔍', '🌱', '🦋', '🍃'];
  const hash = starter.id ? starter.id.charCodeAt(0) : 0;
  return defaultIcons[hash % defaultIcons.length];
};

// Format duration from minutes
const formatDuration = (minutes) => {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

// Transform starter to action card format
const starterToActionCard = (starter, index) => {
  return {
    id: starter.id,
    text: starter.name,
    description: starter.description,
    icon: getStarterIcon(starter),
    prompt: starter.prompt || starter.description || starter.name,
    hint: getRelevanceHint(starter, index),
    duration: formatDuration(starter.estimatedDuration),
    difficulty: starter.difficulty,
    starterId: starter.id, // Keep reference for navigation
  };
};

const NewProjectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isMobile } = useMobile();
  const [quickStartPrompt, setQuickStartPrompt] = useState('');
  const quickStartInputRef = useRef(null);
  const [shuffleSeed, setShuffleSeed] = useState(0);
  
  // Suggestions visibility - controlled by user intent
  // null = not yet determined (waiting for projects data)
  const [suggestionsVisible, setSuggestionsVisible] = useState(null);

  // Check if user came from FAB (ProjectsPage)
  const cameFromFAB = location.state?.previousPath === '/projects';

  // Hero background - single random image on mount (no carousel)
  const backgroundImages = [
    '/home/photo1.jpeg',
    '/home/photo2.jpeg',
    '/home/photo3.jpeg',
    '/home/photo4.jpeg',
    '/home/photo5.jpeg',
  ];

  // Pick one random image on mount
  const [heroImage] = useState(() => {
    const randomIndex = Math.floor(Math.random() * backgroundImages.length);
    return backgroundImages[randomIndex];
  });

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get display name from user
  const getDisplayName = (user) => {
    if (user?.firstName) return user.firstName;
    if (user?.username) return user.username;
    return 'Explorer';
  };

  // Format greeting name (truncate if too long)
  const formatGreetingName = (name) => {
    if (!name) return 'Explorer';
    return name.length > 32 ? `${name.slice(0, 29).trim()}...` : name;
  };

  // Determine title text
  const displayTitle = user ? `${getGreeting()}, ${formatGreetingName(getDisplayName(user))}` : 'Welcome';

  // Fetch starters for "Do this now" section
  const { data: startersData = [], isLoading: isLoadingStarters } = useQuery({
    queryKey: ['new-page-starters', shuffleSeed],
    queryFn: async () => {
      // Fetch more starters than needed so we can shuffle
      const response = await exploreAPI.getAll({ limit: 12, offset: 0, sort: 'popularity' });
      const starters = response?.data?.data?.projects || [];
      return starters;
    },
    staleTime: 60 * 1000,
  });

  // Transform starters to action cards (pick 3 shuffled)
  const suggestedActions = useMemo(() => {
    if (startersData.length === 0) return [];
    const shuffled = shuffleArray(startersData);
    return shuffled.slice(0, 3).map((starter, index) => starterToActionCard(starter, index));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startersData, shuffleSeed]);

  // Fetch live (ongoing) projects first - priority
  const { data: liveProjectsData = [] } = useQuery({
    queryKey: ['new-page-live-projects'],
    queryFn: async () => {
      const response = await projectAPI.getAll({ status: 'ongoing', limit: 4, offset: 0 });
      return response?.data || [];
    },
    staleTime: 30 * 1000,
  });

  // Fetch additional ongoing projects if we need to fill up to 3 total
  const { data: ongoingProjectsData = [] } = useQuery({
    queryKey: ['new-page-ongoing-projects'],
    queryFn: async () => {
      const response = await projectAPI.getAll({ status: 'ongoing', limit: 3, offset: 4 });
      return response?.data || [];
    },
    enabled: liveProjectsData.length < 3,
    staleTime: 30 * 1000,
  });

  // Combine: live projects first (up to 3), then fill with ongoing (up to 3 total)
  const displayProjects = useMemo(() => {
    const live = liveProjectsData.slice(0, 3);
    const remaining = 3 - live.length;
    const ongoing = ongoingProjectsData.slice(0, Math.max(0, remaining));
    return [...live, ...ongoing];
  }, [liveProjectsData, ongoingProjectsData]);

  // Determine if user has active projects
  const hasActiveProjects = displayProjects.length > 0;

  // Set initial suggestions visibility based on user state
  // Show suggestions by default only if user has NO active projects
  useEffect(() => {
    if (suggestionsVisible === null) {
      setSuggestionsVisible(!hasActiveProjects);
    }
  }, [hasActiveProjects, suggestionsVisible]);

  // Fetch project cover images
  const [projectCovers, setProjectCovers] = useState({});
  
  useEffect(() => {
    const fetchCovers = async () => {
      const covers = {};
      await Promise.all(
        displayProjects.map(async (project) => {
          try {
            const res = await projectAPI.getImages(project.id, { limit: 1, sort: 'latest' });
            const imgs = res?.data?.data?.images || [];
            const image = imgs.find(img => img?.url) || imgs[0];
            if (image?.url) {
              covers[project.id] = image.url;
            }
          } catch {
            // Silent fail
          }
        })
      );
      setProjectCovers(covers);
    };
    
    if (displayProjects.length > 0) {
      fetchCovers();
    }
  }, [displayProjects]);

  const quickStartMutation = useMutation({
    mutationKey: ['quick-start-project'],
    mutationFn: async ({ inputValue, isSurprise = false }) => {
      const { payload } = buildQuickStartProjectPayload(inputValue, isSurprise);
      const projectResponse = await projectAPI.create(payload);
      const project = extractProjectFromResponse(projectResponse);
      const projectId = project?.id;

      if (!projectId) {
        throw new Error('Project created but missing ID');
      }

      const sessionResponse = await chatAPI.startNewSession(projectId);
      const chatId = extractChatIdFromResponse(sessionResponse);

      if (!chatId) {
        throw new Error('Chat session missing ID');
      }

      return { projectId, chatId };
    },
    onSuccess: ({ projectId, chatId }) => {
      setQuickStartPrompt('');
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project-chats', projectId] });
      navigate(`/projects/${projectId}/chats/${chatId}`);
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(message || error?.message || 'Failed to start project');
    },
  });

  // Clone starter mutation
  const cloneStarterMutation = useMutation({
    mutationKey: ['clone-starter'],
    mutationFn: async (starterId) => {
      const cloneResponse = await exploreAPI.clone(starterId);
      const newProjectId = cloneResponse?.data?.data?.projectId;
      
      if (!newProjectId) {
        throw new Error('Failed to clone starter');
      }

      const sessionResponse = await chatAPI.startNewSession(newProjectId);
      const chatId = extractChatIdFromResponse(sessionResponse);

      if (!chatId) {
        throw new Error('Chat session missing ID');
      }

      return { projectId: newProjectId, chatId };
    },
    onSuccess: ({ projectId, chatId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projects/${projectId}/chats/${chatId}`);
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      const message = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(message || error?.message || 'Failed to start activity');
    },
  });

  // Check if input is disabled
  const isInputDisabled = quickStartMutation.isPending || cloneStarterMutation.isPending || !quickStartPrompt.trim();
  const isActionDisabled = quickStartMutation.isPending || cloneStarterMutation.isPending;

  const handleQuickStartSubmit = useCallback(
    (event) => {
      event.preventDefault();

      if (!user) {
        toast.error('Please login to start a project');
        return;
      }

      if (isActionDisabled) {
        return;
      }

      const normalized = normalizeQuickStartText(quickStartPrompt);

      if (!normalized) {
        toast.error('Describe your project idea first');
        return;
      }

      quickStartMutation.mutate({ inputValue: normalized, isSurprise: false });
    },
    [quickStartPrompt, quickStartMutation, user, isActionDisabled]
  );

  const handleSuggestedAction = useCallback((action) => {
    if (!user) {
      toast.error('Please login to start a project');
      return;
    }

    if (isActionDisabled) {
      return;
    }

    // Clone the starter directly
    if (action.starterId) {
      cloneStarterMutation.mutate(action.starterId);
    } else {
      quickStartMutation.mutate({ inputValue: action.prompt, isSurprise: false });
    }
  }, [quickStartMutation, cloneStarterMutation, user, isActionDisabled]);

  const handleShuffleActions = useCallback(() => {
    if (isActionDisabled) return;
    setShuffleSeed(prev => prev + 1);
  }, [isActionDisabled]);

  const handleResumeClick = useCallback(
    (projectId) => {
      navigate(`/projects/${projectId}`, {
        state: {
          previousPath: '/new',
          transitionDirection: 'back',
        },
      });
    },
    [navigate]
  );

  const handleBrowseStarters = useCallback(() => {
    navigate('/starters', {
      state: {
        previousPath: '/new',
        transitionDirection: 'forward',
      },
    });
  }, [navigate]);

  const handleBrowseProjects = useCallback(() => {
    navigate('/projects', {
      state: {
        previousPath: '/new',
        transitionDirection: 'forward',
      },
    });
  }, [navigate]);

  // Auto-focus input when arriving from FAB on desktop only
  useEffect(() => {
    if (cameFromFAB && !isMobile && quickStartInputRef.current) {
      // Small delay to ensure input has rendered
      const timer = setTimeout(() => {
        quickStartInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [cameFromFAB, isMobile]);

  // Generate mock "last action" for projects
  const getProjectLastAction = (project) => {
    // In production, this would come from the project's actual activity
    const mockActions = [
      'Check for yellowing leaves',
      'Document new growth',
      'Tighten left bolt',
      'Photograph specimen',
      'Record observations',
    ];
    // Use project id to get consistent mock action
    const index = project.id ? parseInt(project.id.slice(-2), 16) % mockActions.length : 0;
    return mockActions[index];
  };

  return (
    <div className="min-h-full bg-white dark:bg-gray-900 pb-16">
      {/* Hero Section - Comfortable height with single static image */}
      <div className="relative w-full overflow-hidden min-h-[26rem] sm:min-h-[24rem] lg:min-h-[28rem]">
        {/* Static Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${heroImage}')`,
          }}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/45 to-black/65"></div>

        {/* Bottom Gradient Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-gray-900 to-transparent pointer-events-none"></div>

        {/* Logo - Mobile Only */}
        <div
          className="absolute left-4 z-10 md:hidden"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
        >
          <img src="/logo_white.webp" alt="OKIO" className="h-6 w-auto" />
        </div>

        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-center px-6 py-8 sm:py-10">
          <div className="max-w-4xl w-full mx-auto text-center">
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-5 leading-tight shadow-sm break-words">
              {displayTitle}
            </h1>
            <form onSubmit={handleQuickStartSubmit} className="mt-5 flex flex-col items-center gap-3">
              <div className="relative max-w-xl w-full mx-auto">
                <input
                  ref={quickStartInputRef}
                  type="text"
                  aria-label="What should we start?"
                  className="w-full rounded-full border-2 border-white/60 bg-white/90 text-gray-900 placeholder-gray-500 px-5 py-3.5 pr-14 text-sm md:text-base shadow-xl focus:outline-none focus:ring-2 focus:ring-white/70 focus:border-white/80 dark:bg-gray-900/80 dark:text-white dark:placeholder-gray-300 dark:border-white/40"
                  placeholder="What should we start?"
                  value={quickStartPrompt}
                  onChange={(e) => setQuickStartPrompt(e.target.value)}
                  disabled={isActionDisabled}
                />
                <button
                  type="submit"
                  disabled={isInputDisabled}
                  aria-label="Continue"
                  className={`absolute top-1/2 -translate-y-1/2 right-1.5 inline-flex items-center justify-center rounded-full w-10 h-10 transition focus:outline-none focus:ring-2 focus:ring-white/70 focus:ring-offset-2 focus:ring-offset-transparent ${
                    isInputDisabled
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {(quickStartMutation.isPending || cloneStarterMutation.isPending) ? (
                    <span className="h-4 w-4 border-2 border-gray-400 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                </button>
              </div>
              
              {/* Browse starters link */}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleBrowseStarters}
                  className="text-sm font-medium text-white/80 hover:text-white underline underline-offset-2 transition-colors"
                >
                  Browse starters
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 pb-8 max-w-4xl mx-auto space-y-8">
        
        {/* Active Projects - FIRST for returning users */}
        {displayProjects.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BoltIcon className="w-4 h-4 text-red-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Active projects</h2>
              </div>
              {displayProjects.length >= 3 && (
                <button
                  onClick={handleBrowseProjects}
                  className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  View all Projects →
                </button>
              )}
            </div>
            <div className="space-y-2">
              {displayProjects.map((project, index) => {
                const coverUrl = projectCovers[project.id] || project.images?.[0];
                const isLive = index < liveProjectsData.length;
                const lastAction = getProjectLastAction(project);
                return (
                  <button
                    key={project.id}
                    onClick={() => handleResumeClick(project.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all text-left group active:scale-[0.98]"
                  >
                    {coverUrl ? (
                      <div className="relative flex-shrink-0 w-11 h-11 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 ring-2 ring-gray-200 dark:ring-gray-700 group-hover:ring-blue-300 dark:group-hover:ring-blue-600 transition-all">
                        <img
                          src={getImageUrl(coverUrl)}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {isLive && (
                          <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse ring-2 ring-white dark:ring-gray-800" />
                        )}
                      </div>
                    ) : (
                      <div className="relative flex-shrink-0 w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-700 ring-2 ring-gray-200 dark:ring-gray-700 group-hover:ring-blue-300 dark:group-hover:ring-blue-600 transition-all">
                        {isLive && (
                          <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse ring-2 ring-white dark:ring-gray-800" />
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {project.name}
                        </p>
                        {isLive && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded">
                            Live
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {lastAction}
                      </p>
                    </div>
                    <ArrowRightIcon className="flex-shrink-0 w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors opacity-0 group-hover:opacity-100" />
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Suggestions Section - Request-driven for returning users, default for new users */}
        {suggestionsVisible && suggestedActions.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {hasActiveProjects ? 'Need inspiration?' : 'Do this now'}
                </h2>
              </div>
              <button
                onClick={handleShuffleActions}
                disabled={isActionDisabled || isLoadingStarters}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isActionDisabled || isLoadingStarters
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800'
                }`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoadingStarters ? 'animate-spin' : ''}`} />
                Shuffle
              </button>
            </div>
            <div className="space-y-2">
              {suggestedActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleSuggestedAction(action)}
                  disabled={isActionDisabled}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all text-left group active:scale-[0.98] ${
                    isActionDisabled
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed opacity-60'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-amber-200 dark:hover:border-amber-800 hover:bg-amber-50/30 dark:hover:bg-amber-900/10'
                  }`}
                >
                  <span className="flex-shrink-0 text-2xl">{action.icon}</span>
                  <div className="flex-1 min-w-0">
                    {/* Relevance hint */}
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 italic">
                      {action.hint}
                    </p>
                    {/* Action title */}
                    <p className="font-medium text-gray-900 dark:text-white leading-snug text-sm">
                      {action.text}
                    </p>
                    {/* Duration if available */}
                    {action.duration && (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-gray-400 dark:text-gray-500">
                        <ClockIcon className="w-3 h-3" />
                        {action.duration}
                      </span>
                    )}
                  </div>
                  <ArrowRightIcon className="flex-shrink-0 w-4 h-4 mt-1 text-amber-500 dark:text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Loading state for starters - only show when suggestions should be visible */}
        {suggestionsVisible && isLoadingStarters && suggestedActions.length === 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {hasActiveProjects ? 'Need inspiration?' : 'Do this now'}
              </h2>
            </div>
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                    <div className="flex-1">
                      <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                      <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Quick Navigation */}
        <div className="space-y-2 pt-2">
          {/* View All Projects Link */}
          <button
            onClick={handleBrowseProjects}
            className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all group active:scale-[0.98]"
          >
            <div className="flex items-center gap-3">
              <PaperAirplaneIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">View all Projects</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Manage your projects</p>
              </div>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
          </button>

          {/* Explore Starters Link */}
          <button
            onClick={handleBrowseStarters}
            className="w-full flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group active:scale-[0.98]"
          >
            <div className="flex items-center gap-3">
              <MapIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Explore Starters</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Curated project templates</p>
              </div>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewProjectPage;
